// AdminMembersPage — admin-only member management: list every member, grant
// or revoke admin privileges, and review recent admin-role changes.
//
// This is a UI convenience only — the backend independently enforces admin
// via `require_admin` on every /admin/* route (403 for non-admins), so a
// non-admin who somehow reaches this page still can't mutate anything. The
// page itself is only rendered when useProfile().isAdmin is true (see
// App.tsx's NAV gating). Every grant/revoke is also written to the append-only
// audit log server-side (api/services/authorization.py), which the "Recent
// admin activity" panel below surfaces.
//
// Design rules (AI_LAB_DESIGN_PRINCIPLES.md):
// - 4px grid, semantic color tokens (var(--surface), var(--ink), etc.)
// - Full interactive-state coverage (hover, focus-visible, disabled, busy)
// - Light / dark mode via CSS var() tokens (index.css flips them under
//   :root[data-theme="dark"], so no separate dark-mode rules are needed here)

import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient, ApiError } from "@/lib/api-client";
import type { components } from "@/lib/api-types";
import { useProfile } from "@/lib/useProfile";
// Shared .hub-modal-overlay / .hub-modal classes, reused by RevokeConfirmDialog
// below. Imported explicitly here (not just relied on via App.tsx) so this
// component doesn't depend on import order elsewhere.
import "@/features/hub-feed/hub-feed.css";
import "./admin-members.css";

type MemberProfile = components["schemas"]["MemberProfileResponse"];
type AdminMemberListResponse = components["schemas"]["AdminMemberListResponse"];
type AdminMemberActionResponse = components["schemas"]["AdminMemberActionResponse"];
type AuditEntry = components["schemas"]["AuditEntryResponse"];

const DIVISION_COLOR: Record<string, string> = {
  NBS: "var(--nbs)",
  NFS: "var(--nfs)",
  NDS: "var(--nds)",
  SS: "var(--ss)",
};

const ROLE_CHANGE_ACTIONS = new Set(["grant-admin", "revoke-admin"]);
const SUCCESS_MESSAGE_TTL_MS = 4000;

async function fetchMembers(): Promise<AdminMemberListResponse> {
  return apiClient<AdminMemberListResponse>("/api/v1/admin/members");
}

async function grantAdmin(memberId: string): Promise<AdminMemberActionResponse> {
  return apiClient<AdminMemberActionResponse>(
    `/api/v1/admin/members/${memberId}/grant-admin`,
    { method: "POST" },
  );
}

async function revokeAdmin(memberId: string): Promise<AdminMemberActionResponse> {
  return apiClient<AdminMemberActionResponse>(
    `/api/v1/admin/members/${memberId}/revoke-admin`,
    { method: "POST" },
  );
}

async function fetchRecentActivity(): Promise<AuditEntry[]> {
  return apiClient<AuditEntry[]>("/api/v1/admin/audit?limit=50");
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && typeof err.body === "object" && err.body && "detail" in err.body) {
    return String((err.body as { detail?: unknown }).detail);
  }
  return err instanceof Error ? err.message : fallback;
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  if (diffSec < 60) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// MemberRow
// ---------------------------------------------------------------------------

interface MemberRowProps {
  member: MemberProfile;
  isSelf: boolean;
  isLastAdmin: boolean;
  busy: boolean;
  onGrant: (memberId: string) => void;
  onRequestRevoke: (member: MemberProfile) => void;
}

function MemberRow({ member, isSelf, isLastAdmin, busy, onGrant, onRequestRevoke }: MemberRowProps) {
  const divisionColor = DIVISION_COLOR[member.division] ?? "var(--ink-2)";

  return (
    <li className="admin-members__row" data-testid="admin-member-row" data-member-id={member.id}>
      <span
        className="admin-members__avatar"
        aria-hidden="true"
        style={{ backgroundColor: divisionColor }}
      >
        {member.initials}
      </span>

      <span className="admin-members__identity">
        <span className="admin-members__name">
          {member.name}
          {isSelf && <span className="admin-members__you"> (you)</span>}
        </span>
        <span className="admin-members__role">{member.role}</span>
      </span>

      <span className="admin-members__division" style={{ color: divisionColor }}>
        {member.division}
      </span>

      <span
        className={
          "admin-members__badge" +
          (member.is_admin ? " admin-members__badge--admin" : "")
        }
      >
        {member.is_admin ? "Admin" : "Member"}
      </span>

      {member.is_admin ? (
        <button
          type="button"
          className="admin-members__action admin-members__action--revoke"
          disabled={busy || isLastAdmin}
          aria-label={`Remove admin access from ${member.name}`}
          title={isLastAdmin ? "At least one admin must remain — promote someone else first." : undefined}
          onClick={() => onRequestRevoke(member)}
        >
          Remove admin
        </button>
      ) : (
        <button
          type="button"
          className="admin-members__action admin-members__action--grant"
          disabled={busy}
          aria-label={`Make ${member.name} an admin`}
          onClick={() => onGrant(member.id)}
        >
          Make admin
        </button>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// RevokeConfirmDialog — replaces window.confirm with a themed, dark-mode-aware
// dialog whose button text carries the verb (NN/g: "restate the action in the
// button, not a bare Yes/No").
// ---------------------------------------------------------------------------

interface RevokeConfirmDialogProps {
  member: MemberProfile;
  isSelf: boolean;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function RevokeConfirmDialog({ member, isSelf, busy, onConfirm, onCancel }: RevokeConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="hub-modal-overlay" onClick={onCancel}>
      <div
        className="hub-modal admin-members__confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="admin-revoke-confirm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hub-modal__head">
          <h2 className="hub-modal__title" id="admin-revoke-confirm-title">
            Remove admin access?
          </h2>
        </div>
        <p className="admin-members__confirm-body">
          {isSelf
            ? "You're about to remove your own admin access. You'll lose access to this page unless another admin promotes you back."
            : `${member.name} will no longer be able to manage members, posts, training, or events.`}
        </p>
        <div className="admin-members__confirm-actions">
          <button
            type="button"
            ref={cancelRef}
            className="admin-members__action"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="admin-members__action admin-members__action--revoke"
            disabled={busy}
            onClick={onConfirm}
          >
            Remove admin access
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RecentActivity — surfaces the audit trail the backend writes on every
// grant/revoke, so "every admin action attributable" is actually visible
// somewhere, not just a claim.
// ---------------------------------------------------------------------------

interface RecentActivityProps {
  entries: AuditEntry[];
  membersById: Map<string, MemberProfile>;
}

function RecentActivity({ entries, membersById }: RecentActivityProps) {
  const roleChanges = entries.filter((e) => ROLE_CHANGE_ACTIONS.has(e.action)).slice(0, 5);
  if (roleChanges.length === 0) return null;

  return (
    <section className="admin-members__activity" aria-label="Recent admin activity">
      <h2 className="admin-members__activity-title">Recent admin activity</h2>
      <ul className="admin-members__activity-list">
        {roleChanges.map((entry) => {
          const actorName = membersById.get(entry.actor_id)?.name ?? entry.actor_id;
          const targetName =
            entry.target_id === entry.actor_id
              ? "themselves"
              : (membersById.get(entry.target_id)?.name ?? entry.target_id);
          const verb = entry.action === "grant-admin" ? "made" : "removed admin access from";
          const suffix = entry.action === "grant-admin" ? "an admin" : "";
          return (
            <li key={entry.id} className="admin-members__activity-row">
              <span>
                <strong>{actorName}</strong> {verb} <strong>{targetName}</strong> {suffix}
              </span>
              <span className="admin-members__activity-time">{formatRelativeTime(entry.created_at)}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// AdminMembersPage
// ---------------------------------------------------------------------------

export function AdminMembersPage() {
  const { profile } = useProfile();
  const [members, setMembers] = useState<MemberProfile[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [pendingRevoke, setPendingRevoke] = useState<MemberProfile | null>(null);
  const [activity, setActivity] = useState<AuditEntry[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    fetchMembers()
      .then((result) => setMembers(result.members))
      .catch((err: unknown) => {
        setLoadError(extractErrorMessage(err, "Could not load members."));
      })
      .finally(() => setLoading(false));
  }, []);

  const loadActivity = useCallback(() => {
    // Supplementary panel — fail silently rather than adding a second error
    // banner if the audit endpoint is unreachable.
    fetchRecentActivity()
      .then(setActivity)
      .catch(() => setActivity([]));
  }, []);

  useEffect(() => {
    load();
    loadActivity();
  }, [load, loadActivity]);

  // Auto-dismiss the success message so it reads as a toast, not a permanent
  // banner competing with the (persistent) error state.
  useEffect(() => {
    if (!successMessage) return;
    const timer = window.setTimeout(() => setSuccessMessage(null), SUCCESS_MESSAGE_TTL_MS);
    return () => window.clearTimeout(timer);
  }, [successMessage]);

  const applyUpdate = (updated: MemberProfile) => {
    setMembers((prev) =>
      prev ? prev.map((m) => (m.id === updated.id ? updated : m)) : prev,
    );
  };

  const handleGrant = (memberId: string) => {
    setBusyId(memberId);
    setActionError(null);
    setSuccessMessage(null);
    grantAdmin(memberId)
      .then((result) => {
        applyUpdate(result.member);
        setSuccessMessage(`${result.member.name} is now an admin.`);
        loadActivity();
      })
      .catch((err: unknown) => {
        setActionError(extractErrorMessage(err, "Could not promote member."));
      })
      .finally(() => setBusyId(null));
  };

  const handleRevoke = (memberId: string) => {
    setBusyId(memberId);
    setActionError(null);
    setSuccessMessage(null);
    revokeAdmin(memberId)
      .then((result) => {
        applyUpdate(result.member);
        setSuccessMessage(`Removed admin access for ${result.member.name}.`);
        loadActivity();
      })
      .catch((err: unknown) => {
        setActionError(extractErrorMessage(err, "Could not remove admin access."));
      })
      .finally(() => {
        setBusyId(null);
        setPendingRevoke(null);
      });
  };

  const allMembers = members ?? [];
  const adminCount = allMembers.filter((m) => m.is_admin).length;
  const membersById = new Map(allMembers.map((m) => [m.id, m]));

  const filtered = allMembers.filter((m) =>
    query.trim() === "" ? true : m.name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <div className="admin-members" data-testid="admin-members-page">
      <div className="admin-members__head">
        <div>
          <div className="admin-members__eyebrow">Admin</div>
          <h1 className="admin-members__title">Manage admins</h1>
          <p className="admin-members__subtitle">
            Grant or remove admin privileges for any member. The last remaining
            admin can't be removed.
          </p>
        </div>
        <div className="admin-members__search" role="search">
          <input
            type="text"
            placeholder="Search members…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search members"
          />
        </div>
      </div>

      {members && !loading && !loadError && (
        <p className="admin-members__summary" data-testid="admin-members-summary">
          {allMembers.length} member{allMembers.length === 1 ? "" : "s"} · {adminCount} admin
          {adminCount === 1 ? "" : "s"}
        </p>
      )}

      {successMessage && (
        <p className="admin-members__success" role="status" aria-live="polite" data-testid="admin-members-success">
          {successMessage}
        </p>
      )}

      {actionError && (
        <p className="admin-members__error" role="alert" data-testid="admin-members-action-error">
          {actionError}
        </p>
      )}

      {loading && (
        <div className="admin-members__loading" role="status" aria-live="polite">
          Loading members…
        </div>
      )}

      {loadError && !loading && (
        <div className="admin-members__load-error" role="alert">
          <strong>Could not load members.</strong>
          <p>{loadError}</p>
          <button type="button" className="admin-members__action" onClick={load}>
            Retry
          </button>
        </div>
      )}

      {members && !loading && !loadError && (
        <>
          {allMembers.length === 0 ? (
            <div className="admin-members__empty">No members yet.</div>
          ) : filtered.length === 0 ? (
            <div className="admin-members__empty">
              No members match &ldquo;{query}&rdquo;.
            </div>
          ) : (
            <ul className="admin-members__list" aria-label="All members">
              {filtered.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  isSelf={member.id === profile?.id}
                  isLastAdmin={member.is_admin && adminCount <= 1}
                  busy={busyId === member.id}
                  onGrant={handleGrant}
                  onRequestRevoke={setPendingRevoke}
                />
              ))}
            </ul>
          )}
        </>
      )}

      <RecentActivity entries={activity} membersById={membersById} />

      {pendingRevoke && (
        <RevokeConfirmDialog
          member={pendingRevoke}
          isSelf={pendingRevoke.id === profile?.id}
          busy={busyId === pendingRevoke.id}
          onConfirm={() => handleRevoke(pendingRevoke.id)}
          onCancel={() => setPendingRevoke(null)}
        />
      )}
    </div>
  );
}

export default AdminMembersPage;
